import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { User, Bell, Shield, CreditCard, Save, Check, Eye, EyeOff, Crown, BarChart3, Zap } from 'lucide-react';
import { profileService } from '../lib/supabaseServices';
import { supabase } from '../lib/supabase';

function Settings({ darkMode }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Individual section states
  const [profileData, setProfileData] = useState({
    full_name: '',
    company_name: '',
    phone: '',
    timezone: 'UTC'
  });

  const [notifications, setNotifications] = useState({
    new_replies: true,
    campaign_completion: true,
    weekly_reports: true,
    account_warnings: true,
    daily_limits: true,
    new_features: false
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Saving states for each section
  const [saving, setSaving] = useState({
    profile: false,
    notifications: false,
    password: false
  });

  const [saved, setSaved] = useState({
    profile: false,
    notifications: false,
    password: false
  });

  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const profileData = await profileService.getProfile();
      setProfile(profileData);
      
      // Set form data from profile
      setProfileData({
        full_name: profileData.full_name || '',
        company_name: profileData.company_name || '',
        phone: profileData.phone || '',
        timezone: profileData.timezone || 'UTC'
      });

      setNotifications(profileData.notifications || {
        new_replies: true,
        campaign_completion: true,
        weekly_reports: true,
        account_warnings: true,
        daily_limits: true,
        new_features: false
      });

    } catch (error) {
      console.error('Error loading profile:', error);
      setError('Failed to load profile settings');
    } finally {
      setLoading(false);
    }
  };

  const saveSection = async (section) => {
    setSaving(prev => ({ ...prev, [section]: true }));
    setSaved(prev => ({ ...prev, [section]: false }));
    setError(null);

    try {
      if (section === 'profile') {
        await profileService.updateProfile(profileData);
      } else if (section === 'notifications') {
        await profileService.updateProfile({ notifications });
      } else if (section === 'password') {
        if (!passwordData.currentPassword) {
          throw new Error('Current password is required');
        }
        if (passwordData.newPassword !== passwordData.confirmPassword) {
          throw new Error('Passwords do not match');
        }
        if (passwordData.newPassword.length < 6) {
          throw new Error('Password must be at least 6 characters');
        }
        await profileService.updatePassword(passwordData.currentPassword, passwordData.newPassword);
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      }

      setSaved(prev => ({ ...prev, [section]: true }));
      
      // Reset saved indicator after 3 seconds
      setTimeout(() => {
        setSaved(prev => ({ ...prev, [section]: false }));
      }, 3000);

    } catch (error) {
      console.error(`Error saving ${section}:`, error);
      setError(error.message || `Failed to save ${section} settings`);
    } finally {
      setSaving(prev => ({ ...prev, [section]: false }));
    }
  };

  const getSubscriptionStatus = () => {
    const tier = profile?.subscription_tier || 'free';
    const status = profile?.subscription_status || 'inactive';
    const accountsLimit = profile?.subscription_accounts_limit || 0;
    const quantity = profile?.subscription_quantity || 1;
    const periodEnd = profile?.subscription_current_period_end;
    const willCancel = profile?.subscription_cancel_at_period_end || false;
    
    // Calculate price based on tier and quantity
    let price = '$0';
    let planName = 'Free';
    
    switch (tier) {
      case 'starter':
        price = '$27';
        planName = 'Starter';
        break;
      case 'growth':
        const basePrice = quantity * 20; // $20 per account
        price = `$${basePrice}`;
        planName = `Growth (${quantity} accounts)`;
        break;
      case 'scale':
        price = '$397';
        planName = 'Scale';
        break;
      default:
        price = '$0';
        planName = 'Free';
    }
    
    return {
      tier,
      plan: planName,
      status,
      price,
      accountsLimit,
      quantity,
      periodEnd: periodEnd ? new Date(periodEnd).toLocaleDateString() : null,
      willCancel
    };
  };

  const subscription = getSubscriptionStatus();

  // Handle manage subscription (redirect to Stripe Customer Portal)
  const handleManageSubscription = async () => {
    if (subscription.tier === 'free') {
      // Redirect to pricing page if no subscription
      window.location.href = '/pricing';
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('create-portal-session', {
        body: {
          returnUrl: window.location.href
        }
      });

      if (error) throw error;

      // Redirect to Stripe Customer Portal
      window.location.href = data.url;
    } catch (error) {
      console.error('Error creating portal session:', error);
      setError('Failed to open billing portal. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="p-4 lg:p-6">
        <div className={`${darkMode ? 'text-primary' : 'text-gray-900'} text-center py-8`}>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4">Loading settings...</p>
        </div>
      </div>
    );
  }

  const SaveButton = ({ section, onClick, disabled = false }) => (
    <button
      onClick={onClick}
      disabled={disabled || saving[section]}
      className={`px-4 py-2 rounded font-medium flex items-center space-x-2 transition-all duration-200 ${
        saved[section]
          ? darkMode 
            ? 'bg-green-500/20 text-green-400 border border-green-500/50' 
            : 'bg-green-50 text-green-700 border border-green-200'
          : saving[section]
          ? darkMode 
            ? 'bg-blue-500/20 text-blue-400 border border-blue-500/50' 
            : 'bg-blue-50 text-blue-700 border border-blue-200'
          : darkMode 
            ? 'bg-accent hover:bg-accent-hover text-primary border border-accent' 
            : 'bg-blue-500 hover:bg-blue-600 text-white border border-blue-500'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      {saved[section] ? (
        <>
          <Check className="w-4 h-4" />
          <span>Saved</span>
        </>
      ) : saving[section] ? (
        <>
          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
          <span>Saving...</span>
        </>
      ) : (
        <>
          <Save className="w-4 h-4" />
          <span>Save</span>
        </>
      )}
    </button>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-4 lg:p-6 max-w-4xl mx-auto"
    >
      {/* Header */}
      <div className="mb-8">
        <h1 className={`text-2xl font-bold ${darkMode ? 'text-primary' : 'text-gray-900'} mb-2`}>
          Settings
        </h1>
        <p className={`${darkMode ? 'text-secondary' : 'text-gray-600'}`}>
          Manage your account settings and preferences
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className={`mb-6 p-4 ${darkMode ? 'bg-red-500/20 border-red-500/50 text-red-300' : 'bg-red-50 border-red-200 text-red-700'} border rounded`}>
          {error}
        </div>
      )}

      <div className="space-y-6">
        {/* Profile Information */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className={`${darkMode ? 'bg-surface border-border' : 'bg-white border-gray-200'} rounded p-6 shadow-sm border`}
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <User className={`w-5 h-5 ${darkMode ? 'text-accent' : 'text-accent'}`} />
              <h3 className={`text-lg font-semibold ${darkMode ? 'text-primary' : 'text-gray-900'}`}>
                Profile Information
              </h3>
            </div>
            <SaveButton section="profile" onClick={() => saveSection('profile')} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={`block text-sm font-medium ${darkMode ? 'text-secondary' : 'text-gray-700'} mb-2`}>
                Full Name
              </label>
              <input
                type="text"
                value={profileData.full_name}
                onChange={(e) => setProfileData(prev => ({ ...prev, full_name: e.target.value }))}
                className={`w-full px-3 py-2 border rounded ${
                  darkMode 
                    ? 'bg-elevated border-border text-primary placeholder-secondary' 
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                } focus:ring-2 focus:ring-accent focus:border-transparent`}
                placeholder="Enter your full name"
              />
            </div>

            <div>
              <label className={`block text-sm font-medium ${darkMode ? 'text-secondary' : 'text-gray-700'} mb-2`}>
                Company Name
              </label>
              <input
                type="text"
                value={profileData.company_name}
                onChange={(e) => setProfileData(prev => ({ ...prev, company_name: e.target.value }))}
                className={`w-full px-3 py-2 border rounded ${
                  darkMode 
                    ? 'bg-elevated border-border text-primary placeholder-secondary' 
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                } focus:ring-2 focus:ring-accent focus:border-transparent`}
                placeholder="Enter your company name"
              />
            </div>

            <div>
              <label className={`block text-sm font-medium ${darkMode ? 'text-secondary' : 'text-gray-700'} mb-2`}>
                Phone Number
              </label>
              <input
                type="tel"
                value={profileData.phone}
                onChange={(e) => setProfileData(prev => ({ ...prev, phone: e.target.value }))}
                className={`w-full px-3 py-2 border rounded ${
                  darkMode 
                    ? 'bg-elevated border-border text-primary placeholder-secondary' 
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                } focus:ring-2 focus:ring-accent focus:border-transparent`}
                placeholder="+1 (555) 123-4567"
              />
            </div>

            <div>
              <label className={`block text-sm font-medium ${darkMode ? 'text-secondary' : 'text-gray-700'} mb-2`}>
                Timezone
              </label>
              <select
                value={profileData.timezone}
                onChange={(e) => setProfileData(prev => ({ ...prev, timezone: e.target.value }))}
                className={`w-full px-3 py-2 border rounded ${
                  darkMode 
                    ? 'bg-slate-700 border-slate-600 text-white' 
                    : 'bg-white border-gray-300 text-gray-900'
                } focus:ring-2 focus:ring-accent focus:border-transparent`}
              >
                <option value="UTC">UTC</option>
                <option value="America/New_York">Eastern Time</option>
                <option value="America/Chicago">Central Time</option>
                <option value="America/Denver">Mountain Time</option>
                <option value="America/Los_Angeles">Pacific Time</option>
                <option value="Europe/London">London</option>
                <option value="Europe/Paris">Paris</option>
                <option value="Asia/Tokyo">Tokyo</option>
              </select>
            </div>
          </div>
        </motion.div>

        {/* Notification Preferences */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className={`${darkMode ? 'bg-surface border-border' : 'bg-white border-gray-200'} rounded p-6 shadow-sm border`}
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <Bell className={`w-5 h-5 ${darkMode ? 'text-accent' : 'text-accent'}`} />
              <h3 className={`text-lg font-semibold ${darkMode ? 'text-primary' : 'text-gray-900'}`}>
                Notification Preferences
              </h3>
            </div>
            <SaveButton section="notifications" onClick={() => saveSection('notifications')} />
          </div>

          <div className="space-y-4">
            {[
              { key: 'new_replies', label: 'New replies to campaigns', description: 'Get notified when prospects respond to your messages' },
              { key: 'campaign_completion', label: 'Campaign completion', description: 'Get notified when campaigns are completed' },
              { key: 'weekly_reports', label: 'Weekly reports', description: 'Receive weekly performance summaries' },
              { key: 'account_warnings', label: 'Account warnings', description: 'Get alerted about account issues or limits' },
              { key: 'daily_limits', label: 'Daily limit alerts', description: 'Get notified when approaching daily message limits' },
              { key: 'new_features', label: 'New features & updates', description: 'Learn about new platform features and improvements' }
            ].map((item) => (
              <div key={item.key} className="flex items-center justify-between py-3">
                <div className="flex-1">
                  <h4 className={`font-medium ${darkMode ? 'text-primary' : 'text-gray-900'}`}>
                    {item.label}
                  </h4>
                  <p className={`text-sm ${darkMode ? 'text-secondary' : 'text-gray-600'}`}>
                    {item.description}
                  </p>
                </div>
                <button
                  onClick={() => setNotifications(prev => ({ ...prev, [item.key]: !prev[item.key] }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    notifications[item.key] 
                      ? darkMode ? 'bg-accent' : 'bg-accent' 
                      : darkMode ? 'bg-border' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      notifications[item.key] ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Security Settings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className={`${darkMode ? 'bg-surface border-border' : 'bg-white border-gray-200'} rounded p-6 shadow-sm border`}
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <Shield className={`w-5 h-5 ${darkMode ? 'text-accent' : 'text-accent'}`} />
              <h3 className={`text-lg font-semibold ${darkMode ? 'text-primary' : 'text-gray-900'}`}>
                Security Settings
              </h3>
            </div>
            <SaveButton 
              section="password" 
              onClick={() => saveSection('password')}
              disabled={!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
            />
          </div>

          <div className="space-y-4">
            <div>
              <label className={`block text-sm font-medium ${darkMode ? 'text-secondary' : 'text-gray-700'} mb-2`}>
                Current Password
              </label>
              <div className="relative">
                <input
                  type={showPasswords.current ? "text" : "password"}
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                  className={`w-full px-3 py-2 pr-10 border rounded ${
                    darkMode 
                      ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400' 
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                  } focus:ring-2 focus:ring-accent focus:border-transparent`}
                  placeholder="Enter current password"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                  className={`absolute inset-y-0 right-0 pr-3 flex items-center ${
                    darkMode ? 'text-secondary hover:text-primary' : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <label className={`block text-sm font-medium ${darkMode ? 'text-secondary' : 'text-gray-700'} mb-2`}>
                New Password
              </label>
              <div className="relative">
                <input
                  type={showPasswords.new ? "text" : "password"}
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                  className={`w-full px-3 py-2 pr-10 border rounded ${
                    darkMode 
                      ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400' 
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                  } focus:ring-2 focus:ring-accent focus:border-transparent`}
                  placeholder="Enter new password"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                  className={`absolute inset-y-0 right-0 pr-3 flex items-center ${
                    darkMode ? 'text-secondary hover:text-primary' : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <label className={`block text-sm font-medium ${darkMode ? 'text-secondary' : 'text-gray-700'} mb-2`}>
                Confirm New Password
              </label>
              <div className="relative">
                <input
                  type={showPasswords.confirm ? "text" : "password"}
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  className={`w-full px-3 py-2 pr-10 border rounded ${
                    darkMode 
                      ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400' 
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                  } focus:ring-2 focus:ring-accent focus:border-transparent`}
                  placeholder="Confirm new password"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                  className={`absolute inset-y-0 right-0 pr-3 flex items-center ${
                    darkMode ? 'text-secondary hover:text-primary' : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {passwordData.newPassword && passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword && (
              <p className={`text-sm ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
                Passwords do not match
              </p>
            )}

            {passwordData.newPassword && passwordData.newPassword.length > 0 && passwordData.newPassword.length < 6 && (
              <p className={`text-sm ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
                Password must be at least 6 characters
              </p>
            )}
          </div>
        </motion.div>

        {/* Subscription Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className={`${darkMode ? 'bg-surface border-border' : 'bg-white border-gray-200'} rounded p-6 shadow-sm border`}
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <CreditCard className={`w-5 h-5 ${darkMode ? 'text-accent' : 'text-accent'}`} />
              <h3 className={`text-lg font-semibold ${darkMode ? 'text-primary' : 'text-gray-900'}`}>
                Subscription & Billing
              </h3>
            </div>
            {subscription.tier !== 'free' && (
              <div className={`inline-flex items-center space-x-2 px-3 py-1 rounded-full text-sm font-medium ${
                subscription.status === 'active' 
                  ? darkMode ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-green-100 text-green-800 border border-green-200'
                  : darkMode ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' : 'bg-yellow-100 text-yellow-800 border border-yellow-200'
              }`}>
                {subscription.willCancel ? (
                  <>
                    <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></div>
                    <span>Cancelling</span>
                  </>
                ) : (
                  <>
                    <div className={`w-2 h-2 rounded-full ${subscription.status === 'active' ? 'bg-emerald-500' : 'bg-amber-500'}`}></div>
                    <span className="capitalize">{subscription.status}</span>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Plan Information */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div>
              <h4 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'} mb-3`}>Current Plan</h4>
              <div className="flex items-center space-x-3 mb-2">
                {subscription.tier === 'starter' && <Zap className={`w-5 h-5 ${darkMode ? 'text-accent' : 'text-accent'}`} />}
                {subscription.tier === 'growth' && <BarChart3 className={`w-5 h-5 ${darkMode ? 'text-accent' : 'text-accent'}`} />}
                {subscription.tier === 'scale' && <Crown className={`w-5 h-5 ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} />}
                {subscription.tier === 'free' && <User className={`w-5 h-5 ${darkMode ? 'text-muted' : 'text-gray-600'}`} />}
                <span className={`text-xl font-bold ${
                  subscription.tier === 'starter' ? darkMode ? 'text-accent' : 'text-accent'
                  : subscription.tier === 'growth' ? darkMode ? 'text-accent' : 'text-accent'
                  : subscription.tier === 'scale' ? darkMode ? 'text-accent' : 'text-accent'
                  : darkMode ? 'text-muted' : 'text-gray-600'
                }`}>
                  {subscription.plan}
                </span>
              </div>
              <div className={`text-2xl font-bold ${darkMode ? 'text-primary' : 'text-gray-900'} mb-1`}>
                {subscription.price}<span className="text-lg text-muted">/month</span>
              </div>
              {subscription.tier === 'growth' && (
                <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                  ${(parseInt(subscription.price.replace('$', '')) / subscription.quantity).toFixed(2)} per account
                </p>
              )}
            </div>

            <div>
              <h4 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'} mb-3`}>Account Usage</h4>
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <span className={`text-sm ${darkMode ? 'text-secondary' : 'text-gray-600'}`}>
                      Accounts in campaigns
                    </span>
                    <span className={`text-sm font-medium ${darkMode ? 'text-primary' : 'text-gray-900'}`}>
                      {subscription.accountsLimit === 999999 ? 'Unlimited' : `0 of ${subscription.accountsLimit}`}
                    </span>
                  </div>
                  {subscription.accountsLimit !== 999999 && subscription.accountsLimit > 0 && (
                    <div className={`w-full h-2 rounded-full ${darkMode ? 'bg-elevated' : 'bg-gray-200'}`}>
                      <div 
                        className={`h-2 rounded-full ${
                          subscription.tier === 'starter' ? 'bg-accent'
                          : subscription.tier === 'growth' ? 'bg-accent'
                          : 'bg-accent'
                        }`}
                        style={{ width: '0%' }} // TODO: Calculate actual usage
                      ></div>
                    </div>
                  )}
                </div>
                
                {subscription.tier !== 'free' && subscription.periodEnd && (
                  <div>
                    <span className={`text-sm ${darkMode ? 'text-secondary' : 'text-gray-600'}`}>
                      {subscription.willCancel ? 'Subscription ends' : 'Next billing date'}
                    </span>
                    <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {subscription.periodEnd}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className={`pt-4 border-t ${darkMode ? 'border-slate-700' : 'border-gray-200'} flex flex-col sm:flex-row gap-3`}>
            <button 
              onClick={handleManageSubscription}
              className={`flex-1 px-4 py-2 rounded font-medium transition-colors ${
                subscription.tier === 'free'
                  ? darkMode 
                    ? 'bg-accent hover:bg-accent-hover text-primary' 
                    : 'bg-accent hover:bg-accent-hover text-primary'
                  : darkMode
                    ? 'bg-slate-700 hover:bg-slate-600 text-white border border-slate-600'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-900 border border-gray-300'
              }`}
            >
              {subscription.tier === 'free' ? 'Upgrade Plan' : 'Manage Subscription'}
            </button>
            
            {subscription.tier === 'free' && (
              <button 
                onClick={() => window.location.href = '/pricing'}
                className={`px-4 py-2 rounded font-medium transition-colors ${
                  darkMode
                    ? 'bg-slate-700 hover:bg-slate-600 text-white border border-slate-600'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-900 border border-gray-300'
                }`}
              >
                View Pricing
              </button>
            )}
          </div>

          {/* Free Tier Benefits */}
          {subscription.tier === 'free' && (
            <div className={`mt-4 p-4 rounded ${darkMode ? 'bg-slate-900/50' : 'bg-gray-50'}`}>
              <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-gray-600'} mb-2`}>
                <strong>Free Tier Limitations:</strong>
              </p>
              <ul className={`text-sm ${darkMode ? 'text-slate-400' : 'text-gray-600'} space-y-1`}>
                <li>• Cannot add accounts to campaigns</li>
                <li>• Limited to account connections only</li>
                <li>• No automation features</li>
              </ul>
            </div>
          )}
        </motion.div>
      </div>
    </motion.div>
  );
}

export default Settings;